import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { scriptService } from '../services/scriptService';

interface ScriptPartResult {
  id: string;
  part_id: string;
  part_name: string;
  script_part_text: string;
  script_name: string;
  created_at: string;
  script_id: string;
  text_length: number;
  usage_count: number;
}

interface UseExistingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPart: (part: ScriptPartResult) => void;
  partId?: string;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  height: 80vh;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #444;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  color: #cccccc;
  
  &:hover {
    color: #ffffff;
  }
`;

const FilterSection = styled.div`
  padding: 20px;
  border-bottom: 1px solid #444;
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
  background: #1a1a1a;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-weight: 500;
  font-size: 14px;
  color: #ffffff;
`;

const Select = styled.select`
  padding: 6px 10px;
  border: 1px solid #444;
  border-radius: 4px;
  font-size: 14px;
  background: #333;
  color: #ffffff;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const SearchInput = styled.input`
  padding: 6px 10px;
  border: 1px solid #444;
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
  background: #333;
  color: #ffffff;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
  
  &::placeholder {
    color: #888;
  }
`;

const ResultsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #2a2a2a;
`;

const ResultsTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ResultRow = styled.div<{ $isExpanded?: boolean }>`
  border: 1px solid #444;
  border-radius: 6px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.2s;
  background: #1a1a1a;
  
  &:hover {
    background-color: #333;
    border-color: #555;
  }
  
  &.selected {
    background-color: #003d6b;
    border-color: #007bff;
  }
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 8px;
`;

const PartName = styled.div`
  font-weight: 600;
  color: #ffffff;
`;

const MeditationName = styled.div`
  color: #cccccc;
  font-size: 14px;
`;

const ResultMeta = styled.div`
  display: flex;
  gap: 15px;
  color: #aaa;
  font-size: 12px;
  margin-bottom: 10px;
`;

const PreviewText = styled.div<{ $isExpanded?: boolean }>`
  font-size: 13px;
  color: #cccccc;
  line-height: 1.4;
  max-height: ${props => props.$isExpanded ? 'none' : '60px'};
  overflow: ${props => props.$isExpanded ? 'visible' : 'hidden'};
  text-overflow: ellipsis;
  white-space: ${props => props.$isExpanded ? 'pre-wrap' : 'nowrap'};
`;

const NoResults = styled.div`
  text-align: center;
  color: #888;
  padding: 40px;
  font-style: italic;
`;

const LoadingState = styled.div`
  text-align: center;
  color: #888;
  padding: 40px;
`;

const ModalFooter = styled.div`
  padding: 20px;
  border-top: 1px solid #444;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: #1a1a1a;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  font-size: 14px;
  cursor: pointer;
  font-weight: 600;
  
  ${props => props.variant === 'primary' ? `
    background: #007bff;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
    
    &:disabled {
      background: #555;
      cursor: not-allowed;
    }
  ` : `
    background: #444;
    color: #ffffff;
    
    &:hover {
      background: #555;
    }
  `}
`;

export const UseExistingModal: React.FC<UseExistingModalProps> = ({ isOpen, onClose, onSelectPart, partId }) => {
  const [scope, setScope] = useState<'my' | 'all'>('my');
  const [sortBy, setSortBy] = useState<'usage_desc' | 'usage_asc' | 'date_desc' | 'date_asc'>('usage_desc');
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<ScriptPartResult[]>([]);
  const [selectedPart, setSelectedPart] = useState<ScriptPartResult | null>(null);
  const [loading, setLoading] = useState(false);

  const loadResults = async () => {
    setLoading(true);
    try {
      const searchResults = await scriptService.searchScriptParts({
        scope,
        sort: sortBy,
        keyword: keyword.trim() || undefined,
        part_id: partId
      });
      setResults(searchResults.parts);
    } catch (error) {
      console.error('Error searching script parts:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadResults();
    }
  }, [isOpen, scope, sortBy, keyword]);

  const handleUseSelected = () => {
    if (selectedPart) {
      onSelectPart(selectedPart);
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getWordCount = (text: string) => {
    return text.trim().split(/\s+/).length;
  };

  const handleRowClick = (part: ScriptPartResult) => {
    if (selectedPart?.id === part.id) {
      // Toggle expansion if already selected
      setSelectedPart({ ...selectedPart });
    } else {
      setSelectedPart(part);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Use Existing Script Part</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <FilterSection>
          <FilterGroup>
            <FilterLabel>Show:</FilterLabel>
            <Select value={scope} onChange={e => setScope(e.target.value as 'my' | 'all')}>
              <option value="my">My Meditations</option>
              <option value="all">All</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Sort By:</FilterLabel>
            <Select value={sortBy} onChange={e => setSortBy(e.target.value as any)}>
              <option value="usage_desc">Times Used 📉</option>
              <option value="usage_asc">Times Used 📈</option>
              <option value="date_desc">Date 📉</option>
              <option value="date_asc">Date 📈</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Keyword:</FilterLabel>
            <SearchInput
              type="text"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              placeholder="Search..."
            />
          </FilterGroup>
        </FilterSection>

        <ResultsContainer>
          {loading ? (
            <LoadingState>Loading...</LoadingState>
          ) : results.length === 0 ? (
            <NoResults>No script parts found</NoResults>
          ) : (
            <ResultsTable>
              {results.map(part => (
                <ResultRow
                  key={part.id}
                  className={selectedPart?.id === part.id ? 'selected' : ''}
                  onClick={() => handleRowClick(part)}
                  $isExpanded={selectedPart?.id === part.id}
                >
                  <ResultHeader>
                    <div>
                      <PartName>{part.part_name}</PartName>
                      <MeditationName>from "{part.script_name}"</MeditationName>
                    </div>
                  </ResultHeader>
                  <ResultMeta>
                    <span>{getWordCount(part.script_part_text)} words</span>
                    <span>Used {part.usage_count} times</span>
                    <span>Created {formatDate(part.created_at)}</span>
                  </ResultMeta>
                  <PreviewText $isExpanded={selectedPart?.id === part.id}>
                    {part.script_part_text}
                  </PreviewText>
                </ResultRow>
              ))}
            </ResultsTable>
          )}
        </ResultsContainer>

        <ModalFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUseSelected}
            disabled={!selectedPart}
          >
            Use Selected
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};